import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

/**
 * Markdown-lite: the deliberately small renderer used for AI-written text.
 * Text nodes only — never innerHTML — so model output can't inject markup.
 *
 * Supported: `#`-`####` headings, `-`/`•` bullets, `1.` numbered rows, blank
 * lines, and inline **bold** / *italic* / `code`. Tables, links, images and
 * fences are NOT supported on purpose; prompts for these surfaces forbid them.
 *
 * `chat` keeps headings modest (a chat bubble); `document` gives a report page
 * real typographic hierarchy.
 */

/** Render **bold** / *italic* / `code` segments — plain text nodes only. */
export function renderInline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) return <code key={i} className="rounded bg-bg-muted px-1 font-mono text-[0.85em]">{part.slice(1, -1)}</code>;
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) return <em key={i}>{part.slice(1, -1)}</em>;
    return part;
  });
}

const HEADING_CLASS: Record<'chat' | 'document', Record<2 | 3 | 4, string>> = {
  chat: {
    2: 'mt-2 block text-[1.05em] font-bold',
    3: 'mt-2 block font-bold',
    4: 'mt-1.5 block font-semibold',
  },
  document: {
    2: 'mt-7 block border-b border-border pb-1.5 text-xl font-bold first:mt-0',
    3: 'mt-5 block text-base font-bold',
    4: 'mt-4 block text-sm font-bold uppercase tracking-wide text-fg-muted',
  },
};

/** Line-by-line markdown-lite. Kept as a function so chat surfaces can call it directly. */
export function renderMarkdownLite(text: string, variant: 'chat' | 'document' = 'chat'): ReactNode {
  return text.split('\n').map((line, i) => {
    if (line.trim() === '') return <span key={i} className="block h-2" aria-hidden />;
    const heading = /^\s*(#{1,4})\s+/.exec(line);
    if (heading) {
      // `#` promotes to `##`: one title per page belongs to the page, not the model.
      const level = Math.max(heading[1]!.length, 2) as 2 | 3 | 4;
      return (
        <span key={i} role="heading" aria-level={level} className={HEADING_CLASS[variant][level > 4 ? 4 : level]}>
          {renderInline(line.slice(heading[0].length))}
        </span>
      );
    }
    const bullet = /^\s*[-•]\s+/.exec(line);
    if (bullet) {
      return (
        <span key={i} className="flex gap-1.5">
          <span aria-hidden className="text-fg-muted">•</span>
          <span className="min-w-0 flex-1">{renderInline(line.slice(bullet[0].length))}</span>
        </span>
      );
    }
    const ordered = /^\s*(\d{1,2})[.)]\s+/.exec(line);
    if (ordered) {
      return (
        <span key={i} className="flex gap-1.5">
          <span aria-hidden className="font-mono text-xs leading-5 text-fg-muted">{ordered[1]}.</span>
          <span className="min-w-0 flex-1">{renderInline(line.slice(ordered[0].length))}</span>
        </span>
      );
    }
    return <span key={i} className="block">{renderInline(line)}</span>;
  });
}

export interface MarkdownLiteProps {
  text: string;
  variant?: 'chat' | 'document';
  className?: string;
}

export function MarkdownLite({ text, variant = 'chat', className }: MarkdownLiteProps): JSX.Element {
  return (
    <div className={cn(variant === 'document' ? 'text-[15px] leading-7' : 'text-sm leading-6', className)}>
      {renderMarkdownLite(text, variant)}
    </div>
  );
}
